import React from 'react';
import { Star } from 'lucide-react';
import { CSATOverview } from '../../types/dashboard';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';

// Star-level colour scale
const RATING_COLORS: Record<number, string> = {
  5: '#10b981',
  4: '#22c55e',
  3: '#eab308',
  2: '#f97316',
  1: '#ef4444',
};

interface CSATCardProps {
  csatOverview: CSATOverview | null;
}

const CSATCard: React.FC<CSATCardProps> = ({ csatOverview }) => {
  const avgRating      = csatOverview?.average_rating ?? 0;
  const totalResponses = csatOverview?.total_responses ?? 0;
  const ratingDist     = csatOverview?.rating_distribution ?? [];

  return (
    <Card className="h-full bg-gradient-to-t from-primary/5 to-card shadow-sm">
      <CardHeader>
        <CardTitle>Customer Satisfaction</CardTitle>
        <CardDescription>CSAT score · Last 30 days</CardDescription>
        <CardAction>
          <Badge
            variant="outline"
            className={cn(
              'gap-1 font-semibold',
              avgRating >= 4
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                : avgRating >= 3
                  ? 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300'
                  : avgRating > 0
                    ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300'
                    : 'border-border text-muted-foreground',
            )}
          >
            <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
            {avgRating > 0 ? avgRating.toFixed(1) : 'N/A'}
          </Badge>
        </CardAction>
      </CardHeader>

      <CardContent>
        {/* Score + stars */}
        <div className="mb-6 text-center">
          <p className="text-5xl font-bold tabular-nums tracking-tight text-primary">
            {avgRating > 0 ? avgRating.toFixed(1) : 'N/A'}
          </p>
          <div className="mt-2 flex justify-center gap-0.5">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                className={cn(
                  'h-5 w-5 transition-colors',
                  star <= Math.round(avgRating)
                    ? 'fill-yellow-400 text-yellow-400'
                    : 'fill-muted text-muted',
                )}
              />
            ))}
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">
            Based on{' '}
            <span className="font-semibold text-foreground">{totalResponses}</span>{' '}
            responses
          </p>
        </div>

        {/* Rating distribution bars */}
        {Array.isArray(ratingDist) &&
          ratingDist.map((rating) => (
            <div key={rating.rating} className="mb-3">
              <div className="mb-1 flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <span className="w-3 text-right text-xs font-medium tabular-nums text-foreground">
                    {rating.rating}
                  </span>
                  <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                </div>
                <span className="text-xs tabular-nums text-muted-foreground">
                  {rating.count}{' '}
                  <span className="text-muted-foreground/70">
                    ({rating.percentage.toFixed(0)}%)
                  </span>
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: `${rating.percentage}%`,
                    backgroundColor: RATING_COLORS[rating.rating] ?? '#6b7280',
                  }}
                />
              </div>
            </div>
          ))}
      </CardContent>

      <CardFooter className="border-t border-border pt-4 text-xs text-muted-foreground">
        {totalResponses > 0
          ? `Based on ${totalResponses} survey response${totalResponses !== 1 ? 's' : ''} this period`
          : 'No survey responses yet for this period'}
      </CardFooter>
    </Card>
  );
};

export default CSATCard;
